import { motion, useReducedMotion } from "framer-motion";

interface HighlightedQuoteProps {
  text: string;
  /** A verbatim phrase of `text` to mark. */
  highlight: string;
  /** Seconds to wait after the quote scrolls into view. */
  delay?: number;
}

/**
 * Quote text with one phrase marked in highlighter yellow. The highlight
 * sweeps in left to right when the quote scrolls into view (once), and is
 * simply shown when the visitor prefers reduced motion.
 */
export const HighlightedQuote = ({
  text,
  highlight,
  delay = 0,
}: HighlightedQuoteProps) => {
  const reducedMotion = useReducedMotion();
  const at = text.indexOf(highlight);
  if (at < 0) return <>{text}</>;

  const markStyle = {
    backgroundImage:
      "linear-gradient(transparent 12%, var(--color-fwd-yellow) 12%, var(--color-fwd-yellow) 88%, transparent 88%)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "left center",
    boxDecorationBreak: "clone" as const,
    WebkitBoxDecorationBreak: "clone" as const,
  };

  return (
    <>
      {text.slice(0, at)}
      <motion.mark
        className="bg-transparent text-inherit px-0.5 -mx-0.5"
        style={markStyle}
        initial={{ backgroundSize: reducedMotion ? "100% 100%" : "0% 100%" }}
        whileInView={{ backgroundSize: "100% 100%" }}
        viewport={{ once: true, amount: 0.5 }}
        transition={
          reducedMotion
            ? { duration: 0 }
            : { duration: 0.9, delay: 0.4 + delay, ease: [0.33, 1, 0.68, 1] }
        }
      >
        {highlight}
      </motion.mark>
      {text.slice(at + highlight.length)}
    </>
  );
};
